import { Box, NumberInput, Skeleton, Text, TextInput } from "@mantine/core";
import type { CatalogProvider } from "@hooks/flow/useModelCatalog";
import type { OCROptions } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { ProviderModelSelect } from "../ProviderModelSelect";
import "./OCRConfigTab.scss";

interface OCRConfigTabProps {
    isLoadingProviders: boolean;
    isLoadingModels?: boolean;
    providers: CatalogProvider[];
    models: string[];
    selectedProvider: string;
    modelName: string;
    ocrOptions: OCROptions | null;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
    onOcrOptionsChange: (value: OCROptions | null) => void;
}

/**
 * An OCR step: the model reference selects the analyzer (`prebuilt-layout` for
 * text, tables and structure, `prebuilt-read` for text only), plus the
 * Document Intelligence options the contract accepts.
 */
export const OCRConfigTab = ({
    isLoadingProviders,
    isLoadingModels,
    providers,
    models,
    selectedProvider,
    modelName,
    ocrOptions,
    onProviderChange,
    onModelChange,
    onOcrOptionsChange,
}: OCRConfigTabProps) => {
    const { t } = useTranslation();
    const updateOption = <K extends keyof OCROptions>(
        key: K,
        value: OCROptions[K] | null,
    ) => {
        const next: OCROptions = { ...ocrOptions };
        if (value === null || value === undefined || value === "") {
            delete next[key];
        } else {
            next[key] = value;
        }
        onOcrOptionsChange(Object.keys(next).length > 0 ? next : null);
    };

    if (isLoadingProviders) {
        return (
            <Box className="ocr-config-tab__form">
                <Skeleton height={56} />
                <Skeleton height={56} />
                <Skeleton height={120} />
            </Box>
        );
    }

    return (
        <Box className="ocr-config-tab__form">
            <ProviderModelSelect
                isLoadingProviders={isLoadingProviders}
                isLoadingModels={isLoadingModels}
                providers={providers}
                models={models}
                selectedProvider={selectedProvider}
                modelName={modelName}
                onProviderChange={onProviderChange}
                onModelChange={onModelChange}
            />
            <Text size="xs" c="dimmed" mt="-xs">{t("admin.flowConfigs.ocr.modelHint")}</Text>

            <NumberInput
                label={t("admin.flowConfigs.ocr.minConfidence")}
                description={t("admin.flowConfigs.ocr.minConfidenceHint")}
                value={ocrOptions?.min_ocr_confidence ?? ""}
                min={0}
                max={100}
                onChange={(next) =>
                    updateOption("min_ocr_confidence", next === "" ? null : Number(next))
                }
            />
            <TextInput
                label={t("admin.flowConfigs.ocr.locale")}
                description={t("admin.flowConfigs.ocr.localeHint")}
                placeholder="de-DE"
                value={ocrOptions?.locale ?? ""}
                onChange={(event) => updateOption("locale", event.currentTarget.value.trim() || null)}
            />
            <TextInput
                label={t("admin.flowConfigs.ocr.pages")}
                description={t("admin.flowConfigs.ocr.pagesHint")}
                placeholder="1-3,5"
                value={ocrOptions?.pages ?? ""}
                onChange={(event) => updateOption("pages", event.currentTarget.value.trim() || null)}
            />
        </Box>
    );
};
